'use client';

import type { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@kit/ui/card';
import { DateField } from '@kit/ui/date-field';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@kit/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@kit/ui/select';
import { Trans } from '@kit/ui/trans';

import { CONTRACT_LENGTHS } from '../../constants/audition.constants';
import type { UpdateAuditionInput } from '../../schemas/audition.schema';

// This form stores contract dates as `Date` objects; `DateField` speaks
// `YYYY-MM-DD` strings. Convert at the boundary WITHOUT crossing UTC
// (Critical Rule 19 — no `new Date('YYYY-MM-DD')` / `toISOString()`, which
// shift the day for users west of UTC — the bug the native input had here).
function dateToIso(value: Date | string | null | undefined): string {
  if (!value) return '';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function isoToDate(value: string): Date | null {
  if (!value) return null;
  const [y, m, d] = value.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

interface ContractTabProps {
  form: UseFormReturn<UpdateAuditionInput>;
}

export function ContractTab({ form }: ContractTabProps) {
  const { t } = useTranslation();
  return (
    <Card>
      <CardHeader>
        <CardTitle>
          <Trans i18nKey="auditions:formTabs.contract.title" />
        </CardTitle>
        <CardDescription>
          <Trans i18nKey="auditions:formTabs.contract.description" />
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Contract Type */}
        <FormField
          control={form.control}
          name="contractLength"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                <Trans i18nKey="auditions:formTabs.contract.typeLabel" />
              </FormLabel>
              <Select onValueChange={field.onChange} value={field.value || ''}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue
                      placeholder={t(
                        'auditions:formTabs.contract.typePlaceholder',
                      )}
                    />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {CONTRACT_LENGTHS.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      <div>
                        <div className="font-medium">{type.label}</div>
                        <div className="text-muted-foreground text-xs">
                          {type.description}
                        </div>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Contract Period */}
        <div className="grid gap-4 md:grid-cols-2">
          <FormField
            control={form.control}
            name="contractStartDate"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  <Trans i18nKey="auditions:formTabs.contract.startDateLabel" />
                </FormLabel>
                <FormControl>
                  <DateField
                    name={field.name}
                    value={dateToIso(field.value)}
                    onChange={(value) => field.onChange(isoToDate(value))}
                    clearable
                  />
                </FormControl>
                <FormDescription>
                  <Trans i18nKey="auditions:formTabs.contract.startDateHint" />
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="contractEndDate"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  <Trans i18nKey="auditions:formTabs.contract.endDateLabel" />
                </FormLabel>
                <FormControl>
                  <DateField
                    name={field.name}
                    value={dateToIso(field.value)}
                    onChange={(value) => field.onChange(isoToDate(value))}
                    clearable
                  />
                </FormControl>
                <FormDescription>
                  <Trans i18nKey="auditions:formTabs.contract.endDateHint" />
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </CardContent>
    </Card>
  );
}
